package com.vamvisions.main.core.presentation.markdown

/**
 * A block of a parsed markdown document. The tree is already sanitised: every link and image source
 * passed [LinkPolicy], and anything unsupported (raw HTML included) is plain [MdInline.Text].
 */
sealed interface MdNode {
    /** [level] is 1 to 4; deeper markdown headings are shown as level 4. */
    data class Heading(val level: Int, val inlines: List<MdInline>) : MdNode
    data class Paragraph(val inlines: List<MdInline>) : MdNode

    /** Each item is its own list of blocks, so items can hold paragraphs and nested lists. */
    data class BulletList(val items: List<List<MdNode>>) : MdNode
    data class OrderedList(val start: Int, val items: List<List<MdNode>>) : MdNode

    /** [src] is site-relative or `https`; [alt] is the markdown image text. */
    data class Image(val src: String, val alt: String) : MdNode
    data object Rule : MdNode
    data class Table(val header: List<TableCell>, val rows: List<List<TableCell>>) : MdNode
}

data class TableCell(val inlines: List<MdInline>)

sealed interface MdInline {
    data class Text(val value: String) : MdInline
    data class Strong(val children: List<MdInline>) : MdInline
    data class Emphasis(val children: List<MdInline>) : MdInline
    data class Code(val value: String) : MdInline

    /** [href] is `http`, `https`, `mailto` or a site-relative path. */
    data class Link(val href: String, val children: List<MdInline>) : MdInline

    /** A hard line break: two trailing spaces or a backslash at the end of a line. */
    data object LineBreak : MdInline
}
